import { createPerson } from '../factory/personFactory';
import { syncHouseAssignments, ensureHouses, ensurePeopleHoused } from '../sync/houseSync';
import { syncJobsFromPeople } from '../sync/jobsSync';
import { syncPopulation } from '../sync/populationSync';
import { returnUnusedTools } from './toolsManager';
import { getFreePopulation } from './populationMetrics';
import { getHouseCapacity } from '../utils/house';
import type { Person, Settlement } from '../types';

export function addPerson(settlement: Settlement): Person | null {
    ensureHouses(settlement);
    const capacity = getHouseCapacity();
    const targetHouse = settlement.houses.find((house) => house.people.length < capacity);
    if (!targetHouse) return null;
    const person = createPerson(settlement);
    person.houseId = targetHouse.id;
    settlement.people.push(person);
    targetHouse.people.push(person.id);
    syncPopulation(settlement);
    return person;
}

export function removePersonById(settlement: Settlement, personId: string): boolean {
    const index = settlement.people.findIndex((person) => person.id === personId);
    if (index < 0) return false;
    const [removed] = settlement.people.splice(index, 1);
    if (removed?.houseId) {
        const house = settlement.houses.find((item) => item.id === removed.houseId);
        if (house) {
            house.people = house.people.filter((id) => id !== removed.id);
        }
    }
    syncPopulation(settlement);
    syncJobsFromPeople(settlement);
    returnUnusedTools(settlement);
    return true;
}

export function removePeople(settlement: Settlement, count: number): number {
    if (!Number.isFinite(count) || count <= 0) return 0;
    let remaining = Math.min(count, settlement.people.length);
    while (remaining > 0 && settlement.people.length > 0) {
        const person = settlement.people[settlement.people.length - 1];
        if (!person) break;
        removePersonById(settlement, person.id);
        remaining -= 1;
    }
    return count - remaining;
}

export function removeFreePeople(settlement: Settlement, count: number): number {
    if (!Number.isFinite(count) || count <= 0) return 0;
    let remaining = Math.min(count, getFreePopulation(settlement));
    for (let i = settlement.people.length - 1; i >= 0 && remaining > 0; i -= 1) {
        const person = settlement.people[i];
        if (!person || person.job) continue;
        removePersonById(settlement, person.id);
        remaining -= 1;
    }
    return count - remaining;
}

export function setPopulation(settlement: Settlement, target: number): void {
    ensureHouses(settlement);
    const capacity = settlement.houses.length * getHouseCapacity();
    const desired = Math.min(Math.max(0, Math.floor(target)), capacity);
    while (settlement.people.length > desired) {
        removePeople(settlement, 1);
    }
    while (settlement.people.length < desired) {
        const added = addPerson(settlement);
        if (!added) break;
    }
    syncPopulation(settlement);
    syncJobsFromPeople(settlement);
    syncHouseAssignments(settlement);
    ensurePeopleHoused(settlement);
}

export function applyPopulationLoss(settlement: Settlement, deaths: number): boolean {
    if (!Number.isFinite(deaths) || deaths <= 0) return false;
    const removed = removePeople(settlement, deaths);
    return removed > 0;
}
